import {
    ASSET_CLASS_IDS,
    MEASURE_SLUGS,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import {
    AggregateFunctions,
    AggregateQueryInterval,
    AggregateQueryMode,
    ApiQuery,
    Filter,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import { mergeApiQueries } from "~/modules/filters/mergeApiQueries";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { DistributionType, DistributionTypes } from "~/types";
import { MetricTypes } from "./types";
import { AssetTimeseriesColumns } from "~/modules/asset_timeseries_v4/types";

// // // //

export const METRIC_TO_FILTER: Record<MetricTypes, Filter> = {
    [MetricTypes.TotalValue]: {
        operator: FilterOperators.Equals,
        field: TokenTimeseriesColumns.measureSlug,
        value: MEASURE_SLUGS.bridged_token_value_dollar,
    },
    [MetricTypes.AssetHolders]: {
        operator: FilterOperators.Equals,
        field: TokenTimeseriesColumns.measureSlug,
        value: MEASURE_SLUGS.holding_addresses_count,
    },
    [MetricTypes.ActiveAddresses]: {
        operator: FilterOperators.Equals,
        field: TokenTimeseriesColumns.measureSlug,
        value: MEASURE_SLUGS.monthly_active_addresses_count,
    },
    [MetricTypes.TransferVolume]: {
        operator: FilterOperators.Equals,
        field: TokenTimeseriesColumns.measureSlug,
        value: MEASURE_SLUGS.daily_transfer_volume_dollar,
    },
    [MetricTypes.MintBurnVolume]: {
        operator: FilterOperators.Or,
        filters: [
            {
                operator: FilterOperators.Equals,
                field: TokenTimeseriesColumns.measureSlug,
                value: MEASURE_SLUGS.adjusted_daily_mints_dollar,
            },
            {
                operator: FilterOperators.Equals,
                field: TokenTimeseriesColumns.measureSlug,
                value: MEASURE_SLUGS.adjusted_daily_burns_dollar,
            },
        ],
    },
    [MetricTypes.Apy7Day]: {
        operator: FilterOperators.Equals,
        field: AssetTimeseriesColumns.measureSlug,
        value: MEASURE_SLUGS.apy_7_day,
    },
};

export const METRIC_INTERVALS: Record<MetricTypes, AggregateQueryInterval> = {
    [MetricTypes.TotalValue]: AggregateQueryInterval.day,
    [MetricTypes.AssetHolders]: AggregateQueryInterval.day,
    [MetricTypes.ActiveAddresses]: AggregateQueryInterval.month,
    [MetricTypes.TransferVolume]: AggregateQueryInterval.week,
    [MetricTypes.MintBurnVolume]: AggregateQueryInterval.month,
    [MetricTypes.Apy7Day]: AggregateQueryInterval.day,
};

export const EXCLUDE_STABLECOINS_FILTER: Filter = {
    operator: FilterOperators.NotEquals,
    field: TokenTimeseriesColumns.assetClassID,
    value: ASSET_CLASS_IDS.Stablecoin,
};

// // // //

export interface GetApiQueryProps {
    selectedMetric: MetricTypes;
    selectedPlatformType: DistributionType;
    filters: Filter[];
    groupBy: string;
    includeStablecoins: boolean;
    disableStablecoinFilter?: boolean;
}

export function getApiQuery(props: GetApiQueryProps): ApiQuery {
    const {
        selectedMetric,
        selectedPlatformType,
        groupBy,
        includeStablecoins,
    } = props;

    // Base query with metric filter and external filters
    const baseQuery: ApiQuery = {
        sort: {
            direction: SortDirections.desc,
            field: "date",
        },
        pagination: {
            page: 1,
            perPage: 100,
        },
        aggregate: {
            groupBy:
                selectedMetric === MetricTypes.MintBurnVolume
                    ? TokenTimeseriesGroupBy.measure
                    : groupBy,
            aggregateFunction: AggregateFunctions.Sum,
            interval: METRIC_INTERVALS[selectedMetric],
            groupLimit: 50,
            mode: [
                MetricTypes.TransferVolume,
                MetricTypes.MintBurnVolume,
            ].includes(selectedMetric)
                ? AggregateQueryMode.flow
                : AggregateQueryMode.stock,
        },
        filter: {
            operator: FilterOperators.And,
            filters: [METRIC_TO_FILTER[selectedMetric], ...props.filters],
        },
    };

    // Layer on platform type filter
    const withPlatformType =
        selectedPlatformType !== "all"
            ? mergeApiQueries({
                  baseQuery,
                  query: {
                      filter: {
                          operator: FilterOperators.Equals,
                          field: TokenTimeseriesColumns.tokenizationTypeID,
                          value:
                              selectedPlatformType ===
                              DistributionTypes.Distributed
                                  ? TOKENIZATION_TYPE_IDS.OnChainDistributed
                                  : TOKENIZATION_TYPE_IDS.OnChainRepresented,
                      },
                  },
              })
            : baseQuery;

    // Skip all stablecoin filtering when disabled
    if (props.disableStablecoinFilter) {
        return withPlatformType;
    }

    // Exclude stablecoins when toggled off or when viewing "represented" platform type
    const shouldExcludeStablecoins =
        !includeStablecoins ||
        selectedPlatformType === DistributionTypes.Represented;

    if (shouldExcludeStablecoins) {
        return mergeApiQueries({
            baseQuery: withPlatformType,
            query: { filter: EXCLUDE_STABLECOINS_FILTER },
        });
    }

    // When including stablecoins with TotalValue, use an OR filter:
    // (base filters + exclude stablecoins) OR (stablecoin market cap filter)
    // Stablecoins use a different measure (market_cap instead of value)
    if (selectedMetric === MetricTypes.TotalValue) {
        const withExcludedStablecoins = mergeApiQueries({
            baseQuery: withPlatformType,
            query: { filter: EXCLUDE_STABLECOINS_FILTER },
        });

        return {
            ...withPlatformType,
            filter: {
                operator: FilterOperators.Or,
                filters: [
                    withExcludedStablecoins.filter!,
                    {
                        operator: FilterOperators.And,
                        filters: [
                            {
                                operator: FilterOperators.Equals,
                                field: TokenTimeseriesColumns.assetClassID,
                                value: ASSET_CLASS_IDS.Stablecoin,
                            },
                            {
                                operator: FilterOperators.Equals,
                                field: TokenTimeseriesColumns.measureSlug,
                                value: MEASURE_SLUGS.bridged_token_market_cap_dollar,
                            },
                            ...props.filters,
                        ],
                    },
                ],
            },
        };
    }

    // Including stablecoins for non-TotalValue metrics: no stablecoin filter needed
    return withPlatformType;
}
